import React, { useState, useRef, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Typography, Box, CircularProgress,
} from '@mui/material';

export default function RatingModal({ open, onDismiss, onSubmit }) {
  const [hovered, setHovered] = useState(0);
  const [selected, setSelected] = useState(0);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const confirmTimeoutRef = useRef(null);

  useEffect(() => {
    if (open) {
      if (confirmTimeoutRef.current) {
        clearTimeout(confirmTimeoutRef.current);
        confirmTimeoutRef.current = null;
      }
      setHovered(0);
      setSelected(0);
      setComment('');
      setSubmitting(false);
      setDone(false);
    }
  }, [open]);

  const handleSubmit = async () => {
    setSubmitting(true);
    await onSubmit(selected, comment);
    setSubmitting(false);
    setDone(true);
    confirmTimeoutRef.current = setTimeout(() => {
      setDone(false);
      setSelected(0);
      setComment('');
      onDismiss();
    }, 2000);
  };

  const handleDismiss = () => {
    if (confirmTimeoutRef.current) {
      clearTimeout(confirmTimeoutRef.current);
      confirmTimeoutRef.current = null;
    }
    setSelected(0);
    setComment('');
    setHovered(0);
    setSubmitting(false);
    setDone(false);
    onDismiss();
  };

  return (
    <Dialog open={open} onClose={handleDismiss} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ textAlign: 'center', pb: 0 }}>
        <Typography variant="subtitle1" fontWeight={700} color="primary">
          Come valuteresti TRM?
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          La tua opinione ci aiuta a migliorare
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 2, pb: 1 }}>
        {done ? (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <Typography variant="h6">Grazie per il tuo feedback! ⭐</Typography>
          </Box>
        ) : (
          <>
            {/* Stelle */}
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mb: 2 }}>
              {[1, 2, 3, 4, 5].map((star) => (
                <Box
                  key={star}
                  component="span"
                  onMouseEnter={() => setHovered(star)}
                  onMouseLeave={() => setHovered(0)}
                  onClick={() => setSelected(star)}
                  sx={{
                    fontSize: 36,
                    cursor: 'pointer',
                    color: star <= (hovered || selected) ? '#facc15' : '#d1d5db',
                    transition: 'color 0.15s',
                    userSelect: 'none',
                  }}
                >
                  ★
                </Box>
              ))}
            </Box>

            {/* Commento opzionale */}
            <TextField
              label="Commento (opzionale)"
              multiline
              rows={2}
              fullWidth
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              variant="outlined"
              size="small"
              inputProps={{ maxLength: 500 }}
            />
          </>
        )}
      </DialogContent>

      {!done && (
        <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'center', gap: 1 }}>
          <Button onClick={handleDismiss} disabled={submitting} color="inherit">
            Valuta dopo
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={selected === 0 || submitting}
            sx={{
              background: 'linear-gradient(135deg, #0A5C62, #009090)',
              '&:hover': { background: 'linear-gradient(135deg, #084d52, #007a7a)' },
            }}
            startIcon={submitting ? <CircularProgress size={14} color="inherit" /> : null}
          >
            {submitting ? 'Invio...' : 'Invia valutazione'}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
}
